/**
 * realtime/SDKProvider.tsx — boot-order pin (T3034, T3035).
 *
 * Spec anchor: file 05 §L2546–L2548 (cache persistence / hydration).
 *
 *   Cache survives across reload but loads with MISMATCHED SHAPE
 *   (silent decoder corruption) or NEVER persists (cold start every
 *   visit) — both are quiet failures. The load-bearing fix is a
 *   strictly-ordered boot sequence:
 *
 *     1. new QueryClient(...)
 *     2. For every codegen MutationSpec:
 *          qc.setMutationDefaults([spec.key], { mutationFn, scope })
 *        — done BEFORE persistQueryClientRestore so resumePausedMutations
 *        on the next step has the mutationFn it needs.
 *     3. await persistQueryClientRestore({ queryClient: qc, persister })
 *     4. POST-RESTORE / PRE-RESUME (T3035):
 *          install sentinel Promises into the inflight Map for every
 *          paused mutation, keyed by scope.id, resolving when the
 *          mutation settles. Fresh same-scope mutate() during drain
 *          awaits the sentinel.
 *     5. await qc.resumePausedMutations()
 *
 *   This module is INTERFACE-ONLY — no React renderer here. The
 *   implementation lives in the platform-specific provider files (web
 *   vs RN) so the persister can be swapped without touching the boot
 *   sequence. Tests pin every step via `runSDKBootSequence(...)`.
 */

export type MutationFn<Args = unknown, Result = unknown> = (args: Args) => Promise<Result>;

export interface CodegenMutationSpec<Args = unknown, Result = unknown> {
  readonly key: string;
  readonly scopeKey: string;
  readonly mutationFn: MutationFn<Args, Result>;
}

/** Minimal QueryClient shape this module needs — no React import. */
export interface BootQueryClient {
  setMutationDefaults(
    key: readonly unknown[],
    options: { mutationFn: MutationFn; scope?: { id: string } },
  ): void;
  getMutationCache(): {
    getAll(): ReadonlyArray<{
      readonly state: { readonly isPaused: boolean };
      readonly options: { readonly scope?: { readonly id?: string } };
      subscribe(cb: (ev: { readonly type: string }) => void): () => void;
    }>;
  };
  resumePausedMutations(): Promise<unknown>;
}

export interface PersisterLike {
  persistClient(client: unknown): Promise<void>;
  restoreClient(): Promise<unknown>;
  removeClient(): Promise<void>;
}

export interface InflightMapLike {
  set(scopeKey: string, promise: Promise<void>): void;
  get(scopeKey: string): Promise<void> | undefined;
  delete(scopeKey: string): void;
}

/**
 * Deferred Promise helper — pin T3035 step 4. We resolve when the
 * paused mutation settles, NOT when resumePausedMutations resolves
 * (those are different things: resume can complete while individual
 * paused mutations are still settling).
 */
function createDeferred<T>(): {
  promise: Promise<T>;
  resolve: (v: T) => void;
  reject: (e: unknown) => void;
} {
  let resolve!: (v: T) => void;
  let reject!: (e: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

/**
 * Execute the SDKProvider boot sequence. Test harness drives this
 * directly; the React component is a thin wrapper that calls it inside
 * a `useEffect` with an `if (!restored) return <Splash />` gate.
 *
 * Returns the trace of phases executed in order. Tests use the trace to
 * assert the load-bearing ORDER.
 */
export interface BootTrace {
  readonly phases: ReadonlyArray<
    | 'create-client'
    | 'register-mutation-defaults'
    | 'persist-restore'
    | 'install-sentinels'
    | 'resume-paused-mutations'
  >;
  readonly sentinelsInstalled: ReadonlyArray<string>;
}

export interface BootArgs {
  readonly qc: BootQueryClient;
  readonly persister: PersisterLike;
  readonly codegenMutationSpecs: ReadonlyArray<CodegenMutationSpec>;
  readonly inflight: InflightMapLike;
}

export async function runSDKBootSequence(args: BootArgs): Promise<BootTrace> {
  const phases: BootTrace['phases'][number][] = [];

  // 1. (caller created the QueryClient — pin the phase to keep the trace
  //    self-describing).
  phases.push('create-client');

  // 2. Register every mutation default BEFORE restore.
  for (const spec of args.codegenMutationSpecs) {
    args.qc.setMutationDefaults([spec.key], {
      mutationFn: spec.mutationFn,
      scope: { id: spec.scopeKey },
    });
  }
  phases.push('register-mutation-defaults');

  // 3. Restore persisted state.
  await args.persister.restoreClient();
  phases.push('persist-restore');

  // 4. Install sentinel promises BEFORE resumePausedMutations (T3035).
  const sentinels: string[] = [];
  for (const m of args.qc.getMutationCache().getAll()) {
    if (!m.state.isPaused) continue;
    const scopeKey = m.options.scope?.id ?? 'default';
    const def = createDeferred<void>();
    args.inflight.set(scopeKey, def.promise);
    sentinels.push(scopeKey);
    const unsubscribe = m.subscribe((ev) => {
      if (ev.type === 'settled') {
        // Idempotent cleanup: only delete if the entry is still OUR
        // sentinel (compare-by-reference, file 05 §L2607).
        if (args.inflight.get(scopeKey) === def.promise) {
          args.inflight.delete(scopeKey);
        }
        def.resolve();
        unsubscribe();
      }
    });
  }
  phases.push('install-sentinels');

  // 5. Resume.
  await args.qc.resumePausedMutations();
  phases.push('resume-paused-mutations');

  return { phases, sentinelsInstalled: sentinels };
}
